/**
 * Helpers de formatação de data/hora sensíveis ao locale do i18next.
 *
 * Centralizar aqui pra evitar `toLocaleDateString('pt-BR', ...)` espalhado.
 * Quando o user troca pra EN/ES, as datas seguem o idioma — incluindo o dia
 * da semana ("sáb."→"Sat.", "sáb."→"sáb."), nomes dos meses (já a parte
 * mais visível pro user) e ordem (dd/mm vs mm/dd nos formatos curtos).
 *
 * Locale resolvido via `i18next` quando você passa o `language` corrente.
 * NUNCA usar `navigator.language` — quebra com user que mudou idioma na app
 * mas o browser fica em outro.
 */

/** Mapeia o language code do i18next pra um BCP-47 que o `Intl` aceita. */
function bcp47(locale: string | undefined): string {
  if (!locale) return 'pt-BR';
  const l = locale.toLowerCase();
  if (l.startsWith('en')) return 'en-US';
  if (l.startsWith('es')) return 'es-ES';
  return 'pt-BR';
}

/** Data + hora curta — usado em listas e cards de partida. */
export function formatDateTime(date: Date | string, locale: string | undefined): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(bcp47(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Só data, formato curto local — ex: 06/06/2026 (pt-BR/es) ou 6/6/2026 (en-US). */
export function formatDate(date: Date | string, locale: string | undefined): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(bcp47(locale), { dateStyle: 'short' }).format(d);
}

/** Só data dd/mm/aaaa (ano completo, dia/mês 2 dígitos) — ex: "15/06/2026". */
export function formatDateNumeric(date: Date | string, locale: string | undefined): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(bcp47(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** Só hora HH:mm no formato do locale — ex: "00:57" (pt/es), "12:57 AM" (en-US). */
export function formatTime(date: Date | string, locale: string | undefined): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(bcp47(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Data com dia da semana abreviado — ex: "sáb., 06/06" (pt-BR), "Sat, 6/6" (en-US). */
export function formatDateWeekday(date: Date | string, locale: string | undefined): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(bcp47(locale), {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

/** Moeda local. Preserva o `currency` ISO; locale só afeta separador/símbolo. */
export function formatCurrency(cents: number, currency: string, locale: string | undefined): string {
  return new Intl.NumberFormat(bcp47(locale), { style: 'currency', currency }).format(cents / 100);
}
